import { useState } from 'react';
import { useWorkspace } from '../contexts/workspace-context';
import { workspaceService } from '../services/workspace-service';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

interface CreateWorkspaceModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

// Generate slug from name
const generateSlug = (name: string): string => {
    return name
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

export function CreateWorkspaceModal({
    open,
    onOpenChange,
}: CreateWorkspaceModalProps) {
    const { refetchWorkspaces, setCurrentWorkspace } = useWorkspace();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            setError('Workspace name is required');
            return;
        }

        const slug = generateSlug(name);
        if (!slug) {
            setError('Please enter a valid workspace name');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const newWorkspace = await workspaceService.createWorkspace({
                name: name.trim(),
                slug,
                description: description.trim() || undefined,
            });

            await refetchWorkspaces();
            setCurrentWorkspace(newWorkspace);

            // Reset form and close
            setName('');
            setDescription('');
            onOpenChange(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create workspace');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setName('');
        setDescription('');
        setError(null);
        onOpenChange(false);
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            setName('');
            setDescription('');
            setError(null);
        }
        onOpenChange(newOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Create Workspace</DialogTitle>
                    <DialogDescription>
                        Create a new workspace to organize your tasks, themes, and features.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <label htmlFor="new-workspace-name" className="text-sm font-medium text-foreground">
                            Workspace name
                        </label>
                        <Input
                            id="new-workspace-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Marketing Team"
                            disabled={isLoading}
                            autoFocus
                        />
                        {name && (
                            <p className="text-xs text-muted-foreground">
                                URL: <span className="text-foreground">{generateSlug(name) || '...'}</span>
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="new-workspace-description" className="text-sm font-medium text-foreground">
                            Description
                            <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                        </label>
                        <Input
                            id="new-workspace-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What is this workspace for?"
                            disabled={isLoading}
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-danger bg-danger/10 px-3 py-2 rounded-component">
                            {error}
                        </div>
                    )}

                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading || !name.trim()}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                'Create workspace'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
